import { useState, useEffect } from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import {
  Send,
  CheckCircle2,
  Eye,
  EyeOff,
  ExternalLink,
  Shield,
  Loader2,
  Mail,
  MessageCircle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import api from '@/services/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

const partnerTypes = [
  {
    value: 'country_manager',
    label: 'Country Manager',
    description: 'Representante oficial de CoreDesk en tu país',
    commission: 'Hasta 15% + bonos',
  },
  {
    value: 'legal_expert',
    label: 'Expertos Legales',
    description: 'Colegios, asociaciones, especialistas certificados',
    commission: 'Hasta 15%',
  },
  {
    value: 'institutional',
    label: 'Firmas e Instituciones',
    description: 'Firmas de abogados, universidades, centros de capacitación',
    commission: 'Hasta 12%',
  },
  {
    value: 'promoter',
    label: 'Promotores Legales',
    description: 'Influencers del sector legal, bloggers, creadores de contenido',
    commission: 'Hasta 10%',
  },
  {
    value: 'knowledge',
    label: 'Contribuidores de Conocimiento',
    description: 'Autores legales, académicos, investigadores',
    commission: 'Hasta 8%',
  },
  {
    value: 'agency',
    label: 'Agencias e Influencers',
    description: 'Agencias de marketing legal, influencers externos al sector',
    commission: 'Hasta 5%',
  },
]

export default function Apply() {
  const [searchParams] = useSearchParams()
  const initialCountry = (searchParams.get('country') || '').toUpperCase()
  const initialType = searchParams.get('type') || ''
  const isCountryManagerMode = initialType === 'country_manager'

  const [isLoading, setIsLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [countries, setCountries] = useState([])
  const [loadingCountries, setLoadingCountries] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [formData, setFormData] = useState({
    organizationName: '',
    organizationType:
      initialType && partnerTypes.some((t) => t.value === initialType) ? initialType : '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    hasWhatsApp: false,
    preferredContact: 'email',
    country: initialCountry,
    website: '',
    message: '',
    userPassword: '',
    confirmPassword: '',
    acceptedGuidelines: false,
    linkExistingUser: false,
  })
  const [errors, setErrors] = useState({})

  const [emailCheck, setEmailCheck] = useState(null)
  const [checkingEmail, setCheckingEmail] = useState(false)

  useEffect(() => {
    (async () => {
      try {
        const response = await api.get('/countries/optimized')
        const data = response.data
        if (data.success && data.countries) {
          const formatted = data.countries
            .filter((c) => c.supported)
            .map((c) => ({ value: c.code, label: c.name }))
            .sort((a, b) => a.label.localeCompare(b.label))
          setCountries(formatted)
        }
      } catch {
        setCountries([
          { value: 'MX', label: 'México' },
          { value: 'ES', label: 'España' },
          { value: 'US', label: 'Estados Unidos' },
        ])
      } finally {
        setLoadingCountries(false)
      }
    })()
  }, [])

  useEffect(() => {
    const email = formData.contactEmail.trim()
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailCheck(null)
      return
    }
    setCheckingEmail(true)
    const t = setTimeout(async () => {
      try {
        const res = await api.get(
          `/partners/check-email?email=${encodeURIComponent(email)}`
        )
        setEmailCheck(res.data?.data || null)
      } catch {
        setEmailCheck(null)
      } finally {
        setCheckingEmail(false)
      }
    }, 500)
    return () => clearTimeout(t)
  }, [formData.contactEmail])

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }))
  }

  const validate = () => {
    const e = {}
    if (!formData.organizationName.trim()) e.organizationName = 'Requerido'
    if (!formData.organizationType) e.organizationType = 'Selecciona el tipo'
    if (!formData.contactName.trim()) e.contactName = 'Requerido'
    if (!formData.contactEmail.trim()) e.contactEmail = 'Requerido'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail))
      e.contactEmail = 'Email inválido'
    if (!formData.country) e.country = 'Selecciona tu país'
    if (!formData.userPassword) e.userPassword = 'Requerida'
    else if (formData.userPassword.length < 8) e.userPassword = 'Mínimo 8 caracteres'
    if (!formData.confirmPassword) e.confirmPassword = 'Confirma tu contraseña'
    else if (formData.userPassword !== formData.confirmPassword)
      e.confirmPassword = 'No coinciden'
    if (!formData.acceptedGuidelines) e.acceptedGuidelines = 'Debes aceptar los lineamientos'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (ev) => {
    ev.preventDefault()
    if (!validate()) return
    setIsLoading(true)
    try {
      const payload = {
        ...formData,
        linkExistingUser: !!(emailCheck?.canLinkExisting && formData.linkExistingUser),
      }
      const response = await api.post('/partners/apply', payload)
      if (response.data?.success) {
        setSubmitted(true)
        toast.success('Solicitud enviada')
      } else {
        toast.error(response.data?.message || 'Error al enviar la solicitud')
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error de conexión. Intenta de nuevo.')
    } finally {
      setIsLoading(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8 pt-safe pb-safe">
        <div className="w-full max-w-md">
          <Card className="p-6 sm:p-8 shadow-md text-center">
            <CheckCircle2 size={48} className="mx-auto text-success mb-3" />
            <h1 className="font-display text-h1 font-semibold text-foreground">
              Solicitud enviada
            </h1>
            <p className="text-body-sm text-subtle mt-2 mb-4">
              Gracias por tu interés. Nos pondremos en contacto contigo en los próximos 2-3
              días hábiles.
            </p>
            <Alert variant="info" className="text-left mb-4">
              <Mail size={16} />
              <AlertDescription>
                Revisa tu correo <strong>{formData.contactEmail}</strong> para los siguientes
                pasos.
              </AlertDescription>
            </Alert>
            <Button asChild size="cta">
              <RouterLink to="/login">Volver al login</RouterLink>
            </Button>
          </Card>
        </div>
      </div>
    )
  }

  const selectedType = partnerTypes.find((t) => t.value === formData.organizationType)

  return (
    <div className="min-h-screen bg-background px-4 py-8 pt-safe pb-safe">
      <div className="mx-auto w-full max-w-2xl">
        <Card className="p-5 sm:p-8 shadow-md">
          {/* Header */}
          <div className="text-center mb-5">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-md bg-primary text-primary-foreground font-display text-h3 font-semibold mb-3">
              CD
            </div>
            <h1 className="font-display text-h1 font-semibold text-foreground leading-tight">
              {isCountryManagerMode
                ? `Aplica como Country Manager${initialCountry ? ` de ${initialCountry}` : ''}`
                : 'Únete al Programa de Partners'}
            </h1>
            <p className="text-body-sm text-subtle mt-1">
              {isCountryManagerMode
                ? 'Representa oficialmente a CoreDesk en tu país.'
                : 'Completa el formulario para solicitar acceso al programa'}
            </p>
          </div>

          {/* Email check banners */}
          {emailCheck?.hasPartner && emailCheck?.canUpgrade && (
            <Alert variant="info" className="mb-3">
              <AlertTitle>Ya eres partner</AlertTitle>
              <AlertDescription>
                Detectamos cuenta partner activa. Inicia sesión y solicita el upgrade desde
                tu perfil.{' '}
                <RouterLink to="/login" className="underline font-medium">
                  Iniciar sesión
                </RouterLink>
              </AlertDescription>
            </Alert>
          )}
          {emailCheck?.hasPartner && !emailCheck?.canUpgrade && (
            <Alert variant="warning" className="mb-3">
              <AlertTitle>Solicitud existente</AlertTitle>
              <AlertDescription>
                Ya hay una solicitud o partner activo con este email
                {emailCheck?.partnerStatus ? ` (${emailCheck.partnerStatus})` : ''}.{' '}
                <RouterLink to="/login" className="underline font-medium">
                  Inicia sesión
                </RouterLink>
              </AlertDescription>
            </Alert>
          )}
          {emailCheck?.canLinkExisting && (
            <Alert variant="info" className="mb-3">
              <AlertTitle>
                Tienes cuenta CoreDesk Pro
                {emailCheck.userType === 'lawyer' ? ' como abogado' : ''}
              </AlertTitle>
              <AlertDescription>
                <p className="mb-2">
                  Detectamos tu cuenta. Vincúlala usando la <strong>misma contraseña</strong>{' '}
                  abajo.
                </p>
                <label className="flex items-start gap-2 cursor-pointer">
                  <Checkbox
                    checked={formData.linkExistingUser}
                    onCheckedChange={(v) => setField('linkExistingUser', !!v)}
                    className="mt-0.5"
                  />
                  <span className="text-body-sm">
                    Sí, vincula mi cuenta CoreDesk Pro existente
                  </span>
                </label>
              </AlertDescription>
            </Alert>
          )}

          {!emailCheck?.exists && (
            <Alert variant="success" className="mb-4">
              <AlertTitle>Beneficios del programa</AlertTitle>
              <AlertDescription>
                {isCountryManagerMode
                  ? 'Comisiones hasta 15% + bonos por meta, herramientas exclusivas y equipo del Country Hub.'
                  : 'Comisiones hasta 15% recurrentes mientras tus referidos permanezcan activos.'}
              </AlertDescription>
            </Alert>
          )}

          <Separator className="mb-4" />

          <form onSubmit={handleSubmit} className="space-y-5">
            <section className="space-y-3">
              <h3 className="text-body font-semibold text-foreground">
                Información de la organización
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="org-name">Nombre de la organización *</Label>
                  <Input
                    id="org-name"
                    value={formData.organizationName}
                    onChange={(e) => setField('organizationName', e.target.value)}
                    aria-invalid={!!errors.organizationName}
                  />
                  {errors.organizationName && (
                    <p className="text-caption text-danger">{errors.organizationName}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="org-type">Tipo de partner *</Label>
                  <Select
                    value={formData.organizationType}
                    onValueChange={(v) => setField('organizationType', v)}
                  >
                    <SelectTrigger id="org-type" aria-invalid={!!errors.organizationType}>
                      <SelectValue placeholder="Selecciona el tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      {partnerTypes.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.organizationType && (
                    <p className="text-caption text-danger">{errors.organizationType}</p>
                  )}
                  {selectedType && (
                    <p className="text-caption text-subtle">
                      {selectedType.description} —{' '}
                      <span className="font-semibold text-primary">
                        Comisión: {selectedType.commission}
                      </span>
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="country">País *</Label>
                  {loadingCountries ? (
                    <Skeleton className="h-12" />
                  ) : (
                    <Select
                      value={formData.country}
                      onValueChange={(v) => setField('country', v)}
                    >
                      <SelectTrigger id="country" aria-invalid={!!errors.country}>
                        <SelectValue placeholder="Selecciona tu país" />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((c) => (
                          <SelectItem key={c.value} value={c.value}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {errors.country && (
                    <p className="text-caption text-danger">{errors.country}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="website">Sitio web</Label>
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => setField('website', e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              </div>
            </section>

            <Separator />

            <section className="space-y-3">
              <h3 className="text-body font-semibold text-foreground">
                Información de contacto
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="contact-name">Nombre completo *</Label>
                  <Input
                    id="contact-name"
                    value={formData.contactName}
                    onChange={(e) => setField('contactName', e.target.value)}
                    aria-invalid={!!errors.contactName}
                  />
                  {errors.contactName && (
                    <p className="text-caption text-danger">{errors.contactName}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="contact-email">Correo electrónico *</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setField('contactEmail', e.target.value)}
                    aria-invalid={!!errors.contactEmail}
                  />
                  {(errors.contactEmail ||
                    checkingEmail ||
                    (emailCheck?.canApply === false && emailCheck?.hasPartner)) && (
                    <p
                      className={cn(
                        'text-caption',
                        errors.contactEmail || emailCheck?.hasPartner
                          ? 'text-danger'
                          : 'text-subtle'
                      )}
                    >
                      {errors.contactEmail ||
                        (checkingEmail ? 'Verificando…' : '') ||
                        (emailCheck?.canApply === false && emailCheck?.hasPartner
                          ? 'Email ya registrado'
                          : '')}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="contact-phone">Teléfono</Label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => setField('contactPhone', e.target.value)}
                    placeholder="+52 55 1234 5678"
                  />
                </div>

                {formData.contactPhone && (
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.hasWhatsApp}
                        onCheckedChange={(v) => setField('hasWhatsApp', !!v)}
                      />
                      <MessageCircle size={16} className="text-success-700 dark:text-success-300" />
                      <span className="text-body-sm">Este número tiene WhatsApp</span>
                    </label>
                    {formData.hasWhatsApp && (
                      <div className="space-y-1.5">
                        <Label>Preferencia de contacto</Label>
                        <div className="flex gap-3">
                          {[
                            { value: 'email', label: 'Email', icon: Mail },
                            { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
                          ].map(({ value, label, icon: Icon }) => (
                            <label
                              key={value}
                              className={cn(
                                'flex flex-1 items-center gap-2 rounded-md border px-3 py-2 cursor-pointer transition-colors',
                                formData.preferredContact === value
                                  ? 'border-primary bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                                  : 'border-line hover:bg-surface-muted dark:hover:bg-neutral-800/60'
                              )}
                            >
                              <input
                                type="radio"
                                name="preferredContact"
                                value={value}
                                checked={formData.preferredContact === value}
                                onChange={() => setField('preferredContact', value)}
                                className="sr-only"
                              />
                              <Icon size={16} />
                              <span className="text-body-sm">{label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>

            <Separator />

            <section className="space-y-3">
              <h3 className="text-body font-semibold text-foreground">
                Credenciales de acceso
              </h3>
              <p className="text-caption text-subtle -mt-1">
                Se usarán para acceder al portal una vez aprobada tu solicitud.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="pass">Contraseña *</Label>
                  <div className="relative">
                    <Input
                      id="pass"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.userPassword}
                      onChange={(e) => setField('userPassword', e.target.value)}
                      aria-invalid={!!errors.userPassword}
                      className="pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-md text-subtle hover:bg-surface-muted dark:hover:bg-neutral-800"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <p
                    className={cn(
                      'text-caption',
                      errors.userPassword ? 'text-danger' : 'text-subtle'
                    )}
                  >
                    {errors.userPassword || 'Mínimo 8 caracteres'}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirm">Confirmar contraseña *</Label>
                  <div className="relative">
                    <Input
                      id="confirm"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => setField('confirmPassword', e.target.value)}
                      aria-invalid={!!errors.confirmPassword}
                      className="pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-10 w-10 items-center justify-center rounded-md text-subtle hover:bg-surface-muted dark:hover:bg-neutral-800"
                    >
                      {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-caption text-danger">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>
            </section>

            <div className="space-y-1.5">
              <Label htmlFor="message">Mensaje (opcional)</Label>
              <Textarea
                id="message"
                rows={3}
                value={formData.message}
                onChange={(e) => setField('message', e.target.value)}
                placeholder="Cuéntanos sobre tu organización…"
              />
            </div>

            <div
              className={cn(
                'rounded-lg border p-4 space-y-3',
                errors.acceptedGuidelines
                  ? 'border-danger bg-danger-50 dark:bg-danger-500/15'
                  : 'border-line bg-surface-muted'
              )}
            >
              <div className="flex items-start gap-2">
                <Shield size={18} className="text-primary shrink-0 mt-0.5" />
                <div>
                  <p className="text-body-sm font-semibold text-foreground">
                    Lineamientos del Programa de Partners
                  </p>
                  <p className="text-caption text-subtle mt-0.5">
                    Lee y acepta los requisitos antes de continuar.
                  </p>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                asChild
                className="w-full sm:w-auto"
              >
                <a
                  href="https://coredeskpro.com/partners#requisitos"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ver lineamientos completos <ExternalLink size={14} />
                </a>
              </Button>

              <label className="flex items-start gap-2 cursor-pointer">
                <Checkbox
                  checked={formData.acceptedGuidelines}
                  onCheckedChange={(v) => setField('acceptedGuidelines', !!v)}
                  className="mt-0.5"
                />
                <span className="text-body-sm text-foreground">
                  He leído y acepto los{' '}
                  <a
                    href="https://coredeskpro.com/partners#requisitos"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary font-medium underline"
                  >
                    Lineamientos del Programa
                  </a>
                  , incluyendo requisitos por nivel y normas de cancelación.
                </span>
              </label>
              {errors.acceptedGuidelines && (
                <p className="text-caption text-danger">{errors.acceptedGuidelines}</p>
              )}
            </div>

            <Button
              type="submit"
              size="cta"
              disabled={
                isLoading ||
                !formData.acceptedGuidelines ||
                emailCheck?.hasPartner === true ||
                (emailCheck?.canLinkExisting && !formData.linkExistingUser)
              }
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Enviando…
                </>
              ) : (
                <>
                  <Send size={16} /> Enviar solicitud
                </>
              )}
            </Button>
          </form>

          <div className="mt-5 pt-5 border-t border-line text-center">
            <p className="text-caption text-subtle">
              ¿Ya tienes cuenta?{' '}
              <RouterLink to="/login" className="text-primary font-medium hover:underline">
                Iniciar sesión
              </RouterLink>
            </p>
          </div>
        </Card>

        <p className="mt-4 text-center text-caption text-subtle">
          © {new Date().getFullYear()} CoreDesk. Todos los derechos reservados.
        </p>
      </div>
    </div>
  )
}
